/** $180 for whole dollars, $179.99 otherwise -- matches the backend copy. */
export const formatCartCap = (cap: number): string =>
  Number.isInteger(cap) ? `$${cap.toLocaleString('en-US')}` : `$${cap.toFixed(2)}`

/** The cap is the largest total that is still ready before close
 * ("up to $X ... $X or less"), so only a cart above it is blocked. Compared in
 * cents: the cart total is a float sum, and 5 x 2.89 + 26 x 3.29 comes out as
 * 99.99000000000001, which would block a cart that is exactly the cap. */
export const isOverCartCap = (cartTotal: number, cap: number): boolean =>
  Math.round(cartTotal * 100) > Math.round(cap * 100)

/**
 * The closing-time block that disables checkout. A server-side cap, when there
 * is one, overrides the client projection. The same flag stops more items
 * being added, so the cart can only shrink until the order fits.
 */
export const isBlockedByClose = (
  cartTotal: number,
  closingCap: { cap: number } | null,
  isPastClose: boolean
): boolean =>
  closingCap ? isOverCartCap(cartTotal, closingCap.cap) : isPastClose

/**
 * The "we won't be able to prepare this item before we close" notice is about
 * the order in the cart, so it only applies when there is something in it. The
 * client projection can still say "past close" for an empty cart (the queue
 * wait alone crosses closing), and an empty cart is told to add items instead.
 * A server-side cap takes over from the projection when there is one.
 */
export const shouldShowPastCloseNotice = ({
  cartCount,
  aboveMaximum,
  hasClosingCap,
  isPastClose,
}: {
  cartCount: number
  aboveMaximum: boolean
  hasClosingCap: boolean
  isPastClose: boolean
}): boolean => cartCount > 0 && !aboveMaximum && !hasClosingCap && isPastClose
